import { logError } from '@/utils/logger';
import { useState, useEffect, useMemo } from 'react';
import { CaretLeft, CaretRight, CalendarBlank, Package, FileText, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Retourne YYYY-MM-DD (UTC)
const dayKey = (d) => new Date(d).toISOString().slice(0, 10);

const urgencyStyle = (days) => {
  if (days <= 7) return { border: 'border-l-[#EF4444]', badge: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30', label: 'Critique' };
  if (days <= 14) return { border: 'border-l-[#F59E0B]', badge: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30', label: 'Attention' };
  return { border: 'border-l-[#3B82F6]', badge: 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30', label: 'À venir' };
};

const AlertsCalendar = () => {
  const [contracts, setContracts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [ca, li] = await Promise.all([
          fetch(`${API}/api/alerts/contracts`, { credentials: 'include' }),
          fetch(`${API}/api/logistics`, { credentials: 'include' }),
        ]);
        if (ca.ok) setContracts(await ca.json());
        if (li.ok) {
          const items = await li.json();
          setLowStock(items.filter((x) => (x.quantity_available ?? 0) <= 5));
        }
      } catch (e) { logError('AlertsCalendar fetch', e); }
      setLoading(false);
    })();
  }, []);

  const contractByDay = useMemo(() => {
    const map = new Map();
    contracts.forEach((c) => {
      const k = dayKey(c.contract_end);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    });
    return map;
  }, [contracts]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // décale pour Lundi = 0
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  // Toujours 42 cellules (6 lignes) pour une hauteur constante entre les mois
  while (cells.length < 42) cells.push(null);

  const todayKey = dayKey(new Date());
  const selectedContracts = selectedDay ? (contractByDay.get(selectedDay) || []) : [];

  const shift = (delta) => setCurrentDate(new Date(year, month + delta, 1));

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="alerts-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#F8FAFC] flex items-center gap-2"><CalendarBlank size={26} weight="duotone" className="text-[#F59E0B]" /> Calendrier des alertes</h1>
          <p className="text-sm text-[#94A3B8]">Contrats expirants et articles en stock bas — anticipez vos renouvellements et réapprovisionnements.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="cal-prev"><CaretLeft size={18} /></Button>
          <div className="min-w-[160px] text-center font-medium text-[#F8FAFC]" data-testid="cal-month">{MONTH_NAMES[month]} {year}</div>
          <Button variant="outline" size="icon" onClick={() => shift(1)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="cal-next"><CaretRight size={18} /></Button>
          <Button variant="outline" onClick={() => { setCurrentDate(new Date()); setSelectedDay(null); }} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="cal-today">Aujourd'hui</Button>
        </div>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#0F172A] border border-[#EF4444]/30 rounded-md p-4 flex items-center gap-3">
          <Warning size={28} weight="duotone" className="text-[#EF4444]" />
          <div>
            <p className="text-xs uppercase tracking-wider text-[#94A3B8]">Contrats critiques (≤7j)</p>
            <p className="text-xl font-bold text-[#F8FAFC]">{contracts.filter((c) => c.days_remaining <= 7).length}</p>
          </div>
        </div>
        <div className="bg-[#0F172A] border border-[#F59E0B]/30 rounded-md p-4 flex items-center gap-3">
          <FileText size={28} weight="duotone" className="text-[#F59E0B]" />
          <div>
            <p className="text-xs uppercase tracking-wider text-[#94A3B8]">Contrats à surveiller (≤30j)</p>
            <p className="text-xl font-bold text-[#F8FAFC]">{contracts.length}</p>
          </div>
        </div>
        <div className="bg-[#0F172A] border border-[#3B82F6]/30 rounded-md p-4 flex items-center gap-3">
          <Package size={28} weight="duotone" className="text-[#3B82F6]" />
          <div>
            <p className="text-xs uppercase tracking-wider text-[#94A3B8]">Articles en stock bas</p>
            <p className="text-xl font-bold text-[#F8FAFC]">{lowStock.length}</p>
          </div>
        </div>
      </div>

      {/* Grille calendrier */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[#1E293B]">
          {DAY_NAMES.map((d) => (
            <div key={d} className="px-2 py-2 text-center text-xs uppercase tracking-wider text-[#94A3B8] border-r border-[#1E293B] last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, idx) => {
            if (!date) return <div key={idx} className="min-h-[92px] border-r border-b border-[#1E293B]" />;
            const key = dayKey(date);
            const items = contractByDay.get(key) || [];
            const isToday = key === todayKey;
            const isSelected = key === selectedDay;
            return (
              <button
                key={key}
                onClick={() => setSelectedDay(isSelected ? null : key)}
                className={`min-h-[92px] p-2 text-left border-r border-b border-[#1E293B] last:border-r-0 hover:bg-[#1E293B]/60 transition-colors ${isSelected ? 'bg-[#2563EB]/20' : ''}`}
                data-testid={`cal-day-${key}`}
              >
                <div className={`text-sm font-medium ${isToday ? 'text-[#F59E0B]' : 'text-[#F8FAFC]'}`}>{date.getDate()}</div>
                <div className="mt-1 space-y-1">
                  {items.slice(0, 2).map((c) => {
                    const st = urgencyStyle(c.days_remaining);
                    return (
                      <div key={c.client_id} className={`text-[10px] px-1.5 py-0.5 rounded truncate border-l-2 bg-[#1E293B] ${st.border}`} title={c.client_name}>
                        {c.client_name}
                      </div>
                    );
                  })}
                  {items.length > 2 && <div className="text-[10px] text-[#64748B]">+{items.length - 2} autres</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Détail du jour sélectionné */}
      {selectedDay && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 space-y-3" data-testid="cal-day-detail">
          <h3 className="text-lg font-semibold text-[#F8FAFC]">Détails du {new Date(selectedDay).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
          {selectedContracts.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Aucune expiration de contrat ce jour-là.</p>
          ) : selectedContracts.map((c) => {
            const st = urgencyStyle(c.days_remaining);
            return (
              <div key={c.client_id} className={`p-3 rounded-md bg-[#1E293B]/50 border-l-4 ${st.border}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-[#F8FAFC]">{c.client_name}</p>
                    <p className="text-xs text-[#94A3B8]">Contrat {c.contract_number || '—'} · Zone {c.zone_id || '—'}</p>
                  </div>
                  <Badge className={st.badge}>J-{c.days_remaining} · {st.label}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stock bas — liste séparée */}
      {lowStock.length > 0 && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 space-y-3">
          <h3 className="text-lg font-semibold text-[#F8FAFC] flex items-center gap-2"><Package size={20} weight="duotone" className="text-[#3B82F6]" /> Articles en stock bas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {lowStock.map((it) => (
              <div key={it.item_id} className="p-3 rounded-md bg-[#1E293B]/50 border border-[#1E293B]">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-[#F8FAFC] truncate">{it.name}</p>
                  <Badge className="bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30">{it.quantity_available ?? 0} restant(s)</Badge>
                </div>
                <p className="text-xs text-[#94A3B8] mt-1">{it.type || '—'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-center text-[#94A3B8]">Chargement...</div>}
    </div>
  );
};

export default AlertsCalendar;
